import { forwardRef, useImperativeHandle } from "react";
import { MdFolderOpen } from "react-icons/md";
import { FormattedMessage } from "react-intl";
import { RefetchFnDynamic, graphql, usePaginationFragment } from "react-relay";
import { generatePath, Link } from "react-router";
import { OperationType } from "relay-runtime";
import { Badge } from "@/kit/ui/badge";
import EntityProfilePic from "../EntityProfilePic";
import LoadMore from "../LoadMore";
import SubmissionStatusBadge from "../SubmissionStatusBadge";
import SubmissionApprovals from "../SubmissionApprovals";
import {
  ProjectVersionApproveButton,
  ProjectVersionRejectButton,
} from "../ProjectVersionApproval";
import { SubmissionsCompetitionApprovalsFragment$key } from "./__generated__/SubmissionsCompetitionApprovalsFragment.graphql";
import {
  formatEntitySubmissionCodePath,
  splitWorkspaceSlug,
} from "@/utils/routing";

const Fragment = graphql`
  fragment SubmissionsCompetitionApprovalsFragment on Competition
  @refetchable(queryName: "CompetitionApprovalsFragmentPaginationQuery")
  @argumentDefinitions(
    cursor: { type: "String" }
    count: { type: "Int", defaultValue: 10 }
    owner: { type: "String!" }
    approvalFilter: { type: "ApprovalFilter" }
    stage: { type: "Int" }
  ) {
    slug
    event(slug: $owner) {
      event {
        slug
      }
    }
    viewerCanApprove: viewerCan(action: CREATE_PROJECT_VERSION_APPROVAL)
    submissions(
      approvalFilter: $approvalFilter
      after: $cursor
      first: $count
      stage: $stage
    ) @connection(key: "CompetitionApprovalsFragment_submissions") {
      edges {
        node {
          id
          entity {
            username
            ...EntityProfilePicFragment @arguments(thumbnail: true)
          }
          stage {
            seq
          }
          workspaceVersions(first: 5) @ifAllowed {
            nodes {
              id
              version
              workspace {
                slug
              }
            }
          }
          latest {
            id
            version
            viewerCanReadFiles: viewerCan(action: READ_PROJECT_VERSION_FILE)
            approvalsCheck: approvals(first: 2) @ifAllowed {
              nodes {
                viewerCanRead: viewerCan(action: READ_PROJECT_VERSION_APPROVAL)
              }
            }
            ...SubmissionStatusBadgeFragment
            ...BaseProjectVersionApprovalButtonFragment
            ...SubmissionApprovalsFragment
          }
        }
      }
    }
  }
`;

export interface SubmissionsProps {
  competition: SubmissionsCompetitionApprovalsFragment$key;
}

export interface SubmissionsHandle {
  refetch: RefetchFnDynamic<
    OperationType,
    SubmissionsCompetitionApprovalsFragment$key
  >;
}

export default forwardRef<SubmissionsHandle, SubmissionsProps>(
  function Submissions(props, ref) {
    const {
      data: competition,
      loadNext,
      hasNext,
      isLoadingNext,
      refetch,
    } = usePaginationFragment(Fragment, props.competition);

    useImperativeHandle(
      ref,
      () => ({
        refetch,
      }),
      [refetch],
    );

    if (competition.submissions.edges.length <= 0) {
      return (
        <p className="p-4 text-gray-400">
          <FormattedMessage defaultMessage="No submissions found" />
        </p>
      );
    }

    const showApprovals = competition.submissions.edges.every(
      ({ node: { latest } }) =>
        latest &&
        latest.approvalsCheck?.nodes.every(
          ({ viewerCanRead }) => viewerCanRead,
        ),
    );

    const showWorkspaces = competition.submissions.edges.some(
      ({ node: { workspaceVersions } }) =>
        (workspaceVersions?.nodes.length ?? 0) > 0,
    );

    return (
      <div className="overflow-x-auto">
        <table className="table-auto w-full">
          <thead>
            <tr>
              <th className="py-3 px-3 bg-gray-100 w-[40%]">
                <FormattedMessage defaultMessage="User" />
              </th>
              <th className="py-3 px-3 bg-gray-100">
                <FormattedMessage defaultMessage="Version" />
              </th>
              <th className="py-3 px-3 bg-gray-100">
                <FormattedMessage defaultMessage="Files" />
              </th>
              {showWorkspaces && (
                <th className="py-3 px-3 bg-gray-100">
                  <FormattedMessage defaultMessage="Workspace" />
                </th>
              )}
              <th className="py-3 px-3 bg-gray-100">
                <FormattedMessage defaultMessage="Evaluation" />
              </th>
              {showApprovals && (
                <th className="py-3 px-3 bg-gray-100">
                  <FormattedMessage defaultMessage="Reviewers" />
                </th>
              )}
              {competition.viewerCanApprove && (
                <th className="py-3 px-3 bg-gray-100 w-[0.01%]">
                  <FormattedMessage defaultMessage="Review" />
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {competition.submissions.edges.map(
              ({
                node: { entity, latest: version, stage, workspaceVersions },
              }) => {
                if (!version) {
                  return null;
                }
                return (
                  <tr key={version.id}>
                    <td className="whitespace-nowrap py-2 px-3">
                      <Link to={`/${entity.username}`} className="inline-block">
                        <div className="flex flex-row min-w-0 items-center hover:cursor-pointer">
                          <div className="pr-4">
                            <EntityProfilePic entity={entity} size="12" />
                          </div>
                          <span>{entity.username}</span>
                        </div>
                      </Link>
                    </td>
                    <td className="text-center py-2 px-3">
                      v{version.version}
                    </td>
                    <td className="text-center py-2 px-3">
                      {version.viewerCanReadFiles && (
                        <Link
                          className="whitespace-nowrap underline"
                          to={formatEntitySubmissionCodePath({
                            owner: competition.event.event.slug,
                            entity: entity.username,
                            slug: competition.slug,
                            fileKind: "PACKAGE",
                            version: version.version,
                            stage: stage.seq,
                          })}
                        >
                          <MdFolderOpen className="inline-block mr-1" />
                          <FormattedMessage defaultMessage="Browse" />
                        </Link>
                      )}
                    </td>
                    {showWorkspaces && (
                      <td className="py-2 px-3">
                        <div className="flex flex-wrap gap-1 justify-center">
                          {workspaceVersions?.nodes.map((wsv) => (
                            <Link
                              key={wsv.id}
                              to={generatePath(
                                "/:owner/:slug/versions/:version",
                                {
                                  ...splitWorkspaceSlug(wsv.workspace.slug),
                                  version: wsv.version,
                                },
                              )}
                            >
                              <Badge>
                                {wsv.workspace.slug} v{wsv.version}
                              </Badge>
                            </Link>
                          ))}
                        </div>
                      </td>
                    )}
                    <td className="whitespace-nowrap py-2 px-3 text-center">
                      <span className="inline-block">
                        <SubmissionStatusBadge projectVersion={version} />
                      </span>
                    </td>
                    {showApprovals && (
                      <td className="py-2 px-3">
                        <SubmissionApprovals submission={version} />
                      </td>
                    )}
                    {competition.viewerCanApprove && (
                      <td className="whitespace-nowrap py-2 px-3 text-center">
                        <span className="inline-flex gap-2">
                          <ProjectVersionApproveButton
                            projectVersion={version}
                          />
                          <ProjectVersionRejectButton
                            projectVersion={version}
                          />
                        </span>
                      </td>
                    )}
                  </tr>
                );
              },
            )}
          </tbody>
        </table>
        <LoadMore
          loadMore={loadNext}
          hasMore={hasNext}
          isLoading={isLoadingNext}
        />
      </div>
    );
  },
);
